<template>
  <div
    :class="[
      'chat-list-item',
      {
        unread: isLastMessageUnread(),
        general: isGeneralChat,
        active,
      },
    ]"
    :data-external="
      isGeneralChat
        ? `chat:All participants-conversation-list-item`
        : `chat:${getParticipantNames}-conversation-list-item`
    "
  >
    <ChatParticipantCount
      :conversation="conversation"
      :participants="chatParticipants"
      :all-participants="allParticipants"
      @show-conversation-participants-modal="$emit('show-conversation-participants-modal')"
    />
    <div class="participants-first-message-wrapper">
      <FriedH4 v-if="isGeneralChat">{{ t('dsr_chat_all_participants') }}</FriedH4>
      <FriedH4
        v-else
        v-tooltip="{
          content: getParticipantNames,
          distance: 6,
          delay: { show: 300, hide: 100 },
        }"
        class="participants-text"
      >
        {{ getParticipantNames }}
      </FriedH4>
      <ChatTypingStatus
        v-if="isTypingInConversation(conversation.id, typing)"
        :typing="typing"
        :current-chat-user="currentChatUser"
        :conversation="conversation"
        compact-view
      />
      <FriedParagraph v-else-if="conversationDraft" class="first-message">
        <span class="draft-text">{{ `${t('draft')}:` }}</span>
        {{ conversationDraft }}</FriedParagraph
      >
      <FriedParagraph v-else-if="conversation.messages" class="first-message">{{
        getLatestMessage(conversation.messages)
      }}</FriedParagraph>
    </div>
    <div class="unread-circle" data-external="chat-unread-circle" />
  </div>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSessionStorage } from '@vueuse/core';
import type {
  ChatParticipant,
  ChatParticipantId,
  Conversation,
  Message,
  MessageDraft,
  Typing,
} from '../types';
import { ConversationType } from '../types';
import { ChatHelper } from '../helpers';
import type { Participant } from '../../types';
import AvatarHelper from '../../helpers/avatar.helper';
import ChatTypingStatus from './ChatTypingStatus.vue';
import ChatParticipantCount from './ChatParticipantCount.vue';

export default defineComponent({
  components: {
    ChatTypingStatus,
    ChatParticipantCount,
  },
  props: {
    conversation: {
      type: Object as PropType<Conversation>,
      default: () => ({}),
    },
    chatParticipants: {
      type: Array as PropType<ChatParticipant[]>,
      default: () => [],
    },
    currentUser: {
      type: Object as PropType<Participant>,
      default: () => ({}),
    },
    participantId: {
      type: String,
      default: '',
    },
    typing: {
      type: Array as PropType<Array<Typing>>,
      default: () => [],
    },
    allParticipants: {
      type: Array as PropType<Array<Participant>>,
      default: () => [],
    },
    draftStorageKey: {
      type: String,
      required: true,
    },
    currentChatUser: {
      type: Object as PropType<ChatParticipantId>,
      default: () =>
        ({
          participantId: '',
          userId: '',
        }) as ChatParticipantId,
    },
    active: { type: Boolean },
  },
  emits: ['show-conversation-participants-modal'],
  setup(props) {
    const { t } = useI18n();
    const draftMessages = useSessionStorage<MessageDraft[]>(props.draftStorageKey, []);

    const conversationDraft = computed(() =>
      ChatHelper.getDraftMessage(draftMessages.value, props.conversation.id)
    );

    const isTypingInConversation = (conversationId: string, typing: Typing[]) =>
      typing.some(
        t =>
          t.conversationId === conversationId &&
          !ChatHelper.isSameParticipant(props.currentChatUser, t.participant.participantId)
      );

    const getParticipantNames = computed(() =>
      ChatHelper.getParticipantNames({
        allParticipants: props.allParticipants,
        conversationParticipantIds: props.conversation?.participantIds,
        currentParticipantId: props.currentChatUser?.participantId,
        currentUserId: props.currentChatUser?.userId,
      })
    );

    const isMessageOwnerCurrentParticipant = (sender: Participant) =>
      sender &&
      [props.currentChatUser?.participantId, props.currentChatUser?.userId].includes(sender.id);

    const isMessageOwnerInConversation = (sender: Participant) =>
      sender &&
      sender.id !== props.currentChatUser?.participantId &&
      sender.id !== props.currentChatUser?.userId;

    const getLatestMessage = (messages: Message[]) => {
      const filteredMessages = messages.filter(message => !message.deletedAt);
      const latestMessage = ChatHelper.getLastMessage(filteredMessages);
      const sender = ChatHelper.getOwnerParticipant(latestMessage, props.allParticipants);
      let firstName = '';

      if (isMessageOwnerCurrentParticipant(sender)) {
        firstName = t('you');
      }

      if (isMessageOwnerInConversation(sender)) {
        firstName = AvatarHelper.getDisplayName({
          firstName: sender.firstName,
          email: sender.email,
          deletedAt: sender.deletedAt,
        });
      }

      if (!sender && latestMessage?.ownerId) {
        firstName = t('dr_chat_removed_participant');
      }

      if (latestMessage?.deletedAt) {
        return `${firstName} ${t('deleted_a_message')}`;
      }

      return latestMessage?.text ? `${firstName}: ${latestMessage?.text}` : '';
    };

    const isLastMessageUnread = () =>
      ChatHelper.isLastMessageUnread(
        props.conversation,
        props.currentUser?.id,
        props.participantId
      );

    const isGeneralChat = computed(() => props.conversation.type === ConversationType.General);

    return {
      getParticipantNames,
      isTypingInConversation,
      getLatestMessage,
      isLastMessageUnread,
      isGeneralChat,
      t,

      getParticipantsDetails: ChatHelper.getParticipantsDetails,
      ConversationType,
      conversationDraft,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '../scss/chat-view';
@import '../scss/chat-list-item';
</style>
